import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import { Crown, Search, Filter, Plus, Edit, Trash2, Eye, Calendar, User, FileText, X, Mail } from 'lucide-react';
import '../../styles/AdminStyles.css';

const FeeWaiversAdmin = () => {
  const { user } = useAuth();
  const [waivers, setWaivers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creatingWaiver, setCreatingWaiver] = useState(false);
  const [newWaiver, setNewWaiver] = useState({
    submission_id: '',
    email: '',
    waiver_type: 'invitation',
    expires_in_days: 30,
    notes: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    invitations: 0,
    members: 0,
    adminOverrides: 0
  });



  useEffect(() => {
    fetchAllWaivers();
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('submissions_details')
        .select(`
          submission_id,
          title,
          collection_id,
          gallery_id,
          collections(title),
          galleries(name)
        `)
        .order('title', { ascending: true });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const fetchAllWaivers = async () => {
    try {
      setLoading(true);
      
      // Since the foreign key relationships aren't automatically detected,
      // we'll fetch the data separately and join manually
      const { data: waivers, error: waiversError } = await supabase
        .from('artist_submission_waivers')
        .select('*')
        .order('created_at', { ascending: false });

      if (waiversError) throw waiversError;

      // Now fetch related data for each waiver
      const enrichedWaivers = await Promise.all(
        waivers.map(async (waiver) => {
          const enrichedWaiver = { ...waiver };

          // Fetch user data
          if (waiver.user_id) {
            const { data: user } = await supabase
              .from('users')
              .select('uuid, email, first_name, last_name')
              .eq('uuid', waiver.user_id)
              .single();
            enrichedWaiver.users = user;
          }

          // Fetch invited_by user data
          if (waiver.invited_by) {
            const { data: invitedByUser } = await supabase
              .from('users')
              .select('uuid, email, first_name, last_name')
              .eq('uuid', waiver.invited_by)
              .single();
            enrichedWaiver.invited_by_users = invitedByUser;
          }

          // Fetch submission data
          if (waiver.submission_id) {
            const { data: submission } = await supabase
              .from('submissions_details')
              .select('submission_id, title, collection_id, gallery_id, submission_deadline')
              .eq('submission_id', waiver.submission_id)
              .single();
            enrichedWaiver.submissions_details = submission;
          }

          return enrichedWaiver;
        })
      );

      const processedWaivers = enrichedWaivers.map(waiver => ({
        ...waiver,
        isExpired: waiver.expires_at ? new Date(waiver.expires_at) < new Date() : false,
        daysUntilExpiry: waiver.expires_at ? 
          Math.ceil((new Date(waiver.expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : null
      }));
      
      setWaivers(processedWaivers);
      calculateStats(processedWaivers);
    } catch (error) {
      console.error('Error fetching waivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (waiversList) => {
    const now = new Date();
    const stats = {
      total: waiversList.length,
      active: waiversList.filter(w => !w.isExpired).length,
      expired: waiversList.filter(w => w.isExpired).length,
      invitations: waiversList.filter(w => w.waiver_type === 'invitation').length,
      members: waiversList.filter(w => w.waiver_type === 'member').length,
      adminOverrides: waiversList.filter(w => w.waiver_type === 'admin_override').length
    };
    setStats(stats);
  };

  const createWaiver = async (e) => {
    e.preventDefault();
    setCreatingWaiver(true);

    try {
      // Validate required fields
      if (!newWaiver.submission_id || !newWaiver.email) {
        throw new Error('Please fill in all required fields');
      }

      // First, find the user by email
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('uuid')
        .eq('email', newWaiver.email)
        .single();

      if (userError) {
        throw new Error('User not found with that email address');
      }

      // Check if waiver already exists
      const { data: existingWaiver } = await supabase
        .from('artist_submission_waivers')
        .select('id')
        .eq('user_id', userData.uuid)
        .eq('submission_id', parseInt(newWaiver.submission_id))
        .single();

      if (existingWaiver) {
        throw new Error('Fee waiver already exists for this user and submission');
      }

      // Create the waiver
      const waiverData = {
        user_id: userData.uuid,
        submission_id: parseInt(newWaiver.submission_id),
        waiver_type: newWaiver.waiver_type,
        invited_by: user.id,
        notes: newWaiver.notes,
        expires_at: new Date(Date.now() + newWaiver.expires_in_days * 24 * 60 * 60 * 1000).toISOString()
      };

      const { error } = await supabase
        .from('artist_submission_waivers')
        .insert([waiverData]);

      if (error) throw error;

      // Create corresponding artist_submission_mapping record for acceptance tracking
      const mappingData = {
        user_id: userData.uuid,  // ✅ This is correct - artist's UUID
        submission_id: parseInt(newWaiver.submission_id),
        purchase_id: null,        // No Stripe purchase (waiver = free)
        paid: true,               // Waiver = free submission
        accepted: true,           // Waiver = automatic acceptance
        acceptance_uuid: crypto.randomUUID() // Generate unique acceptance UUID
      };

      const { error: mappingError } = await supabase
        .from('artist_submission_mapping')
        .insert([mappingData]);

      if (mappingError) {
        console.error('Warning: Created waiver but failed to create mapping record:', mappingError);
        // Don't fail the whole operation, but log the warning
      }

      // Reset form and refresh list
      setNewWaiver({
        submission_id: '',
        email: '',
        waiver_type: 'invitation',
        expires_in_days: 30,
        notes: ''
      });
      setShowCreateForm(false);
      fetchAllWaivers();

      alert('Fee waiver created successfully!');
    } catch (error) {
      console.error('Error creating waiver:', error);
      alert(error.message);
    } finally {
      setCreatingWaiver(false);
    }
  };

  const deleteWaiver = async (waiverId) => {
    if (!confirm('Are you sure you want to delete this fee waiver?')) return;

    try {
      // First, get the waiver details to find the corresponding mapping record
      const { data: waiverData, error: waiverFetchError } = await supabase
        .from('artist_submission_waivers')
        .select('user_id, submission_id')
        .eq('id', waiverId)
        .single();

      if (waiverFetchError) throw waiverFetchError;

      // Delete the waiver
      const { error } = await supabase
        .from('artist_submission_waivers')
        .delete()
        .eq('id', waiverId);

      if (error) throw error;

      // Also delete the corresponding mapping record if it exists
      if (waiverData) {
        const { error: mappingDeleteError } = await supabase
          .from('artist_submission_mapping')
          .delete()
          .eq('user_id', waiverData.user_id)
          .eq('submission_id', waiverData.submission_id);

        if (mappingDeleteError) {
          console.error('Warning: Deleted waiver but failed to delete mapping record:', mappingDeleteError);
          // Don't fail the whole operation, but log the warning
        }
      }
      
      // Refresh the list
      fetchAllWaivers();
      alert('Fee waiver deleted successfully!');
    } catch (error) {
      console.error('Error deleting waiver:', error);
      alert('Failed to delete fee waiver');
    }
  };

  const sendEmailInvitation = async (waiver) => {
    try {
      // Prepare email data for Mandrill template
      const emailData = {
        template_name: 'fee-waiver-invitation', // You'll create this template
        message: {
          to: [
            {
              email: waiver.users?.email,
              name: `${waiver.users?.first_name || ''} ${waiver.users?.last_name || ''}`.trim()
            }
          ],
          global_merge_vars: [
            {
              name: 'ARTIST_FIRST_NAME',
              content: waiver.users?.first_name || 'Artist'
            },
            {
              name: 'ARTIST_LAST_NAME',
              content: waiver.users?.last_name || ''
            },
            {
              name: 'SUBMISSION_TITLE',
              content: waiver.submissions_details?.title || 'Your Submission'
            },
            {
              name: 'WAIVER_TYPE',
              content: waiver.waiver_type.replace('_', ' ')
            },
            {
              name: 'EXPIRY_DATE',
              content: waiver.expires_at ? new Date(waiver.expires_at).toLocaleDateString() : 'No expiry'
            },
            {
              name: 'WAIVER_ID',
              content: waiver.id
            },
            {
              name: 'SUBMISSION_ID',
              content: waiver.submission_id || ''
            },
            {
              name: 'SUBMISSION_URL',
              content: `https://vibrantcast.com/artist/submissions/${waiver.submission_id || ''}/apply?submissionId=${waiver.submission_id || ''}`
            },
            {
              name: 'INVITED_BY_NAME',
              content: waiver.invited_by_users ? `${waiver.invited_by_users.first_name || ''} ${waiver.invited_by_users.last_name || ''}`.trim() : 'VibrantCast Team'
            },
            {
              name: 'SUBMISSION_DEADLINE',
              content: waiver.submissions_details?.submission_deadline ? 
                new Date(waiver.submissions_details.submission_deadline).toLocaleDateString() : 
                'Contact us for details'
            },
            {
              name: 'NOTES',
              content: waiver.notes || ''
            }
          ]
        }
      };

      // Call Supabase Edge Function for sending email
      const { data, error } = await supabase.functions.invoke('send-mandrill-email', {
        body: emailData
      });

      if (error) throw error;

      alert(`Email invitation sent successfully to ${waiver.users?.email}`);
    } catch (error) {
      console.error('Error sending email invitation:', error);
      alert('Failed to send email invitation. Please try again.');
    }
  };

  const getFilteredWaivers = () => {
    let filtered = waivers;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(waiver => 
        waiver.users?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        waiver.users?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        waiver.users?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        waiver.submissions?.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(waiver => waiver.waiver_type === filterType);
    }

    // Filter by status
    if (filterStatus === 'active') {
      filtered = filtered.filter(waiver => !waiver.isExpired);
    } else if (filterStatus === 'expired') {
      filtered = filtered.filter(waiver => waiver.isExpired);
    }

    return filtered;
  };

  const getWaiverTypeColor = (type) => {
    switch (type) {
      case 'invitation': return 'blue';
      case 'member': return 'green';
      case 'admin_override': return 'purple';
      default: return 'gray';
    }
  };

  const getStatusBadge = (waiver) => {
    if (waiver.isExpired) {
      return <span className="status-badge expired">Expired</span>;
    }
    if (waiver.daysUntilExpiry <= 7) {
      return <span className="status-badge warning">Expires Soon</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  // Utility function to fix existing waivers that don't have mapping records
  const fixExistingWaivers = async () => {
    if (!confirm('This will create missing mapping records for existing waivers. Continue?')) return;

    try {
      setLoading(true);
      
      // Get all waivers
      const { data: waivers, error: waiversError } = await supabase
        .from('artist_submission_waivers')
        .select('*');

      if (waiversError) throw waiversError;

      let fixedCount = 0;
      let errorCount = 0;

      // Check each waiver and create mapping record if missing
      for (const waiver of waivers) {
        try {
          // Check if mapping record already exists
          const { data: existingMapping } = await supabase
            .from('artist_submission_mapping')
            .select('id')
            .eq('user_id', waiver.user_id)
            .eq('submission_id', waiver.submission_id)
            .single();

          if (!existingMapping) {
            // Create missing mapping record
            const mappingData = {
              user_id: waiver.user_id,
              submission_id: waiver.submission_id,
              purchase_id: null,
              paid: true,
              accepted: true,
              acceptance_uuid: crypto.randomUUID()
            };

            const { error: mappingError } = await supabase
              .from('artist_submission_mapping')
              .insert([mappingData]);

            if (mappingError) {
              console.error(`Failed to create mapping for waiver ${waiver.id}:`, mappingError);
              errorCount++;
            } else {
              fixedCount++;
            }
          }
        } catch (err) {
          console.error(`Error processing waiver ${waiver.id}:`, err);
          errorCount++;
        }
      }

      alert(`Fixed ${fixedCount} waivers. Errors: ${errorCount}`);
      fetchAllWaivers(); // Refresh the list
    } catch (error) {
      console.error('Error fixing existing waivers:', error);
      alert('Failed to fix existing waivers');
    } finally {
      setLoading(false);
    }
  };

  const getSubmissionDisplayName = (submission) => {
    if (!submission) return 'Unknown';
    let name = submission.title || 'Untitled';
    if (submission.collections?.title) {
      name += ` (${submission.collections.title})`;
    }
    if (submission.galleries?.name) {
      name += ` - ${submission.galleries.name}`;
    }
    return name;
  };

  if (loading) return <div className="loading-container">Loading fee waivers...</div>;

  const filteredWaivers = getFilteredWaivers();

  return (
    <div className="fee-waivers-admin">
      <div className="admin-header">
        <div className="admin-header-left">
          <h1 className="admin-title">
            <Crown className="admin-title-icon" />
            Fee Waivers Management
          </h1>
          <p className="admin-subtitle">Manage fee waivers for invited artists across all submissions</p>
        </div>
        <div className="admin-header-right">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="create-waiver-btn"
          >
            <Plus size={20} />
            {showCreateForm ? 'Cancel' : 'Create New Waiver'}
          </button>
        </div>
      </div>

      {/* Create New Waiver Form */}
      {showCreateForm && (
        <div className="create-waiver-form-container">
          <div className="create-waiver-form-header">
            <h3>Create New Fee Waiver</h3>
            <button
              onClick={() => setShowCreateForm(false)}
              className="close-form-btn"
            >
              <X size={20} />
            </button>
          </div>
          
          <form onSubmit={createWaiver} className="create-waiver-form">
            <div className="form-row">
              <div className="form-group">
                <label>Submission *</label>
                <select
                  value={newWaiver.submission_id}
                  onChange={(e) => setNewWaiver({...newWaiver, submission_id: e.target.value})}
                  required
                  className="form-select"
                >
                  <option value="">Select a submission...</option>
                  {submissions.map((submission) => (
                    <option key={submission.submission_id} value={submission.submission_id}>
                      {getSubmissionDisplayName(submission)}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Artist Email *</label>
                <input
                  type="email"
                  value={newWaiver.email}
                  onChange={(e) => setNewWaiver({...newWaiver, email: e.target.value})}
                  required
                  placeholder="artist@email.com"
                  className="form-input"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Waiver Type</label>
                <select
                  value={newWaiver.waiver_type}
                  onChange={(e) => setNewWaiver({...newWaiver, waiver_type: e.target.value})}
                  className="form-select"
                >
                  <option value="invitation">Invitation</option>
                  <option value="member">Member</option>
                  <option value="admin_override">Admin Override</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Expires In (days)</label>
                <input
                  type="number"
                  value={newWaiver.expires_in_days}
                  onChange={(e) => setNewWaiver({...newWaiver, expires_in_days: parseInt(e.target.value) || 30})}
                  min="1"
                  max="365"
                  className="form-input"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Notes (optional)</label>
              <textarea
                value={newWaiver.notes}
                onChange={(e) => setNewWaiver({...newWaiver, notes: e.target.value})}
                placeholder="Optional notes about this waiver..."
                rows={3}
                className="form-textarea"
              />
            </div>
            
            <div className="form-actions">
              <button
                type="submit"
                disabled={creatingWaiver}
                className="submit-btn"
              >
                {creatingWaiver ? 'Creating...' : 'Create Fee Waiver'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Stats Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-icon total">
            <Crown size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Waivers</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon active">
            <User size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.active}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon expired">
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.expired}</div>
            <div className="stat-label">Expired</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon invitations">
            <FileText size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.invitations}</div>
            <div className="stat-label">Invitations</div>
          </div>
        </div>
      </div>

      {/* Fix Existing Waivers Button */}
      <div className="fix-waivers-section">
        <button
          onClick={fixExistingWaivers}
          disabled={loading}
          className="fix-waivers-btn"
          title="Create missing mapping records for existing waivers"
        >
          🔧 Fix Existing Waivers
        </button>
        <p className="fix-waivers-help">
          This will create missing mapping records for existing waivers so artists can receive acceptance notifications.
        </p>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by artist name, email, or submission title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-controls">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="invitation">Invitations</option>
            <option value="member">Members</option>
            <option value="admin_override">Admin Overrides</option>
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Waivers List */}
      <div className="waivers-list-container">
        <div className="waivers-header">
          <h3>Fee Waivers ({filteredWaivers.length})</h3>
        </div>
        
        {filteredWaivers.length === 0 ? (
          <div className="no-waivers">
            <Crown size={48} />
            <p>No fee waivers found</p>
            <small>Create fee waivers using the button above</small>
          </div>
        ) : (
          <div className="waivers-table">
            <div className="waivers-table-header">
              <div className="waiver-col artist">Artist</div>
              <div className="waiver-col submission">Submission</div>
              <div className="waiver-col type">Type</div>
              <div className="waiver-col status">Status</div>
              <div className="waiver-col dates">Dates</div>
              <div className="waiver-col actions">Actions</div>
            </div>
            
            {filteredWaivers.map((waiver) => (
              <div key={waiver.id} className="waiver-row">
                <div className="waiver-col artist">
                  <div className="artist-info">
                    <div className="artist-name">
                      {waiver.users?.first_name} {waiver.users?.last_name}
                    </div>
                    <div className="artist-email">{waiver.users?.email}</div>
                  </div>
                </div>
                
                <div className="waiver-col submission">
                  <div className="submission-info">
                    <div className="submission-title">
                      {waiver.submissions_details?.title || 'Unknown Submission'}
                    </div>
                    <div className="submission-details">
                      Unknown Gallery
                    </div>
                  </div>
                </div>
                
                <div className="waiver-col type">
                  <span className={`waiver-type-badge ${getWaiverTypeColor(waiver.waiver_type)}`}>
                    {waiver.waiver_type.replace('_', ' ')}
                  </span>
                </div>
                
                <div className="waiver-col status">
                  {getStatusBadge(waiver)}
                </div>
                
                <div className="waiver-col dates">
                  <div className="date-info">
                    <div className="created-date">
                      Created: {new Date(waiver.created_at).toLocaleDateString()}
                    </div>
                    {waiver.expires_at && (
                      <div className="expiry-date">
                        Expires: {new Date(waiver.expires_at).toLocaleDateString()}
                        {waiver.daysUntilExpiry && waiver.daysUntilExpiry > 0 && (
                          <span className="days-remaining"> ({waiver.daysUntilExpiry} days)</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="waiver-col actions">
                  <button
                    onClick={() => sendEmailInvitation(waiver)}
                    className="action-btn email"
                    title="Send email invitation"
                    disabled={!waiver.users?.email}
                  >
                    <Mail size={16} />
                  </button>
                  <button
                    onClick={() => deleteWaiver(waiver.id)}
                    className="action-btn delete"
                    title="Delete waiver"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeeWaiversAdmin;
